import React, { useEffect, useState } from "react";
import DateSelect from "../utils/DateSelect";
import PointsSelect from "../utils/PointsSelect";
import MultiSelect from "../utils/MultiSelect";
import Loader from '../utils/Loader';
import { useDispatch, useSelector } from "react-redux";
import { groupBy, isEmpty } from "lodash";
import { fetchUsersByRole } from "../../action/user";
import { roleId } from "../../constants/app";
import { fetchFilials } from "../../action/handbook";
import { fetchGoods, fetchRawsList, fetchReportRevenue, setReportsFilters } from "../../action";
import ErrorContainer from "../utils/ErrorContainer";
import { fromStore } from "../../selectors";
import { fetchReceipes, getGoodWithReceipeData } from "../../action/recipes";
import { parseNumToFixed } from "../../utils/common";
import SortButton from "../utils/SortButton";
import { utils, writeFileXLSX } from 'xlsx';
import swal from 'sweetalert';
import moment from "moment";

const styles = {
    loading: {
        minHeight: '200px',
        display: 'flex',
        justifyContent: 'center'
    }
};

const colors = {
    A: '#77fca3',
    B: '#ebfc77',
    C: '#ff5252'
}

function ItemRow ({item}) {
    return (
        <tr className='kt-datatable__row'>
            <td className='kt-datatable__cell' style={{ width: '170px' }}>
                <span>{item.category || '-'}</span>
            </td>
            <td className='kt-datatable__cell' style={{ width: 'auto' }}>
                <span>{item.name || '-'}</span>
            </td>
            <td className='kt-datatable__cell' style={item.countABC ? { backgroundColor: colors[item.countABC], width: '150px' } : { width: '150px' }}>
                <span>{item.countABC || '-'}</span>
            </td>
            <td className='kt-datatable__cell' style={item.foodCostABC ? { backgroundColor: colors[item.foodCostABC], width: '150px' } : { width: '150px' }}>
                <span>{item.foodCostABC || '-'}</span>
            </td>
            <td className='kt-datatable__cell' style={item.marginABC ? { backgroundColor: colors[item.marginABC], width: '150px' } : { width: '150px' }}>
                <span>{item.marginABC || '-'}</span>
            </td>
        </tr>
    )
}

export default function ABCAnalysis() {
    const data = useSelector(state => state.reports.abcAnalysis);
    const points = useSelector(state => state.handbook.filials);
    const pointsCount = useSelector(state => state.handbook.count.filials);
    const pointLoading = useSelector(state => state.loaders.filials);
    const goods = useSelector(state => state.good.list);
    const goodsLoading = useSelector(fromStore.loaderGoodsSelector);
    const receipes = useSelector(state => state.recipes.list);
    const raws = useSelector(fromStore.rawListSelector);

    const { result, filters, loading } = data;
    const [ regionalDirectors, setRegionalDirectors ] = useState([]);
    const [ _sort, setSort ] = useState({ field: 'category', sort: 'ASC'});
    const [ renderFilters, setRenderFilters ] = useState(filters);
    const [ tableData, setTableData ] = useState({ goods: [], total: {} });

    const dispatch = useDispatch();

    useEffect(() => {
        const getData = async () => {
            const regDirs = await dispatch(fetchUsersByRole({ role: roleId.regionalDirector }));
            const resultDirs = regDirs.map((dir) => ({ value: dir.id, label: dir.name ? dir.name : dir.username, points: dir.points })).sort((a, b) => a.label.localeCompare(b.label, 'ru'));
            setRegionalDirectors(resultDirs);
            if (!pointLoading && (isEmpty(points) || points.length < pointsCount)) await dispatch(fetchFilials(null, true));
        };

        isEmpty(goods) && dispatch(fetchGoods());
        if (isEmpty(raws)) dispatch(fetchRawsList());
        getData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [goods]);

    useEffect(() => {
        const getData = async () => {
            if (!result || !goods || !raws) return;
            const goodsData = await getRenderData();
            setTableData(goodsData);
        };
        getData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [result, goods, raws, receipes, renderFilters]);

    const handleClickSort = (newFiled) => {
        const { field, sort } = _sort;
        if (newFiled === field) {
            const newSort = sort === 'DESC' ? 'ASC' : 'DESC';
            setSort({field: _sort.field, sort: newSort});
        } else {
            setSort({field: newFiled, sort: 'ASC'});
        }
    }

    const onChangeDateSelect = (e) => {
        if (e && e.min && e.max) {
            const updated = { ...filters};
            updated.created_at_gte = e.min;
            updated.created_at_lte = e.max;
            dispatch(setReportsFilters('abcAnalysis', updated));
        }
    }

    const setFilters = (field) => (data) => {
        const updated = { ...filters, [field]: data};
        dispatch(setReportsFilters('abcAnalysis', updated));
    }

    const onSearch = () => {
        dispatch(fetchReportRevenue({ withGoods: true }, 'abcAnalysis'));
        setRenderFilters(filters);
        if (renderFilters.created_at_gte !== filters.created_at_gte || isEmpty(receipes)) dispatch(fetchReceipes({ _limit: -1, startDate_lte: filters.created_at_lte }));
    }

    const isDisabledSearch = () => {
        if (loading) return true;
        if ((!filters.points || filters.points.length === 0) && (!filters.regionalDirectors || filters.regionalDirectors.length === 0)) return true;
        return false;        
    }

    const getRenderData = async () => {
        if (isEmpty(goods)) return { goods: [], total: {} };

        const getCostPriceGood = (receipeData, field = 'default') => {
            if (isEmpty(receipeData)) return null;
            
            const sum = receipeData.reduce((acc, item) => {
                const rawData = raws.find((raw) => raw.id === item.id);
                if (!rawData) return acc;
                
                const price = parseNumToFixed(rawData.last_price);
                
                if (item.count) {
                    const quantity = item.count[field] ? parseNumToFixed(item.count[field], 3) : 0;
                    acc += price * quantity;
                } else {
                    const quantity = item.quantity && item.quantity[field] ? parseNumToFixed(item.quantity[field], 3) : 0;
                    acc += price * quantity;
                }
                
                return acc;
            }, 0);
            
            return parseNumToFixed(sum);
        }

        const entityGoods = await Promise.all(goods.map(async (good) => {
            const categoryTitle = (good.category && good.category.title) ? good.category.title : 'Не определена';
            const receipeData = await dispatch(getGoodWithReceipeData(good));
            
            if (!isEmpty(good.modifications)) {
                return good.modifications.map((mod) => {
                    const costprice = getCostPriceGood(receipeData, mod.default ? null : mod.id);
                    return {
                        id: good.id,
                        idMod: mod.default ? null : mod.id,
                        name: `${good.name || `Наименование товара id ${good.id} не определено`} ${mod.title || `Наименование модификации id ${mod.id} не определено`}`,
                        category: categoryTitle,
                        receipeData,
                        costprice
                    };
                });
            }
            const costprice = getCostPriceGood(receipeData);
            return [{ id: good.id, name: good.name || `Наименование товара id ${good.id} не определено`, category: categoryTitle, receipeData, costprice }];
        }));
        
        const normalizedGoods = entityGoods.flat();

        const normalizedOrders = (result || []).map((order) => {
            if (order.goods && typeof order.goods === 'string') {
                return { ...order, goods: JSON.parse(order.goods)}
            } else {
                return order;
            }
        });
        
        const reduceWokComponents = (wokEntity, goodId, mainCount = 0) => {
            return Object.entries(wokEntity).reduce((acc, [id, itemData]) => {
                if (String(id) !== goodId) return acc;
                let price = 0;
                if (itemData.data && itemData.data.price) {
                    price = itemData.data.price;
                } else if (itemData.price) {
                    price = itemData.price;
                }

                const count = itemData.count ? itemData.count * mainCount : mainCount;
                acc.count += count
                acc.sum += parseNumToFixed(count * price);
                if (itemData.costpriceGood) acc.costpriceGood += itemData.costpriceGood;
                return acc;
            }, {sum: 0, count: 0, costpriceGood: null });
        }

        const goodWithOrderData = normalizedGoods.map((good) => {
            const orderData = normalizedOrders.reduce((acc, order) => {
                if (!order || isEmpty(order.goods)) return acc;
                const goodId = good.idMod ? `${good.id}_${good.idMod}` : String(good.id);
                const entityGoods = order.goods.filter((item) => String(item.id) === goodId);
                if (!isEmpty(entityGoods)) {
                    const dataOrderGoods = entityGoods.reduce((goodAcc, item) => {
                        goodAcc.count += item.count || 0;
                        let costpriceGood = 0;

                        if (item.costpriceGood) {
                            costpriceGood = item.costpriceGood;
                        } else {
                            costpriceGood = parseNumToFixed(good.costprice * item.count);
                        }

                        goodAcc.costpriceGood += costpriceGood;
                        goodAcc.sum += parseNumToFixed((item.count || 0) * (item.price || 0));
                        return goodAcc;
                    }, { sum: 0, count: 0, costpriceGood: 0});

                    acc.count += dataOrderGoods.count || 0;
                    acc.costpriceGood += dataOrderGoods.costpriceGood || 0;
                    acc.sum += dataOrderGoods.sum || 0;
                } else if (!good.idMod) {
                    const woksGoods = order.goods.filter((item) => item.isWok);
                    if (!woksGoods || woksGoods.length === 0) return acc;
                    const dataOrderGoods = woksGoods.reduce((goodAcc, item) => {
                        if (isEmpty(item.wokData)) return goodAcc;
                        const { main, meat, topping, souce } = item.wokData;
                        if (main && String(main.id) === goodId) {
                            goodAcc.count += item.count;
                            goodAcc.sum += parseNumToFixed((item.count || 0) * (main.price || 0));
                            if (main.costpriceGood) goodAcc.costpriceGood += main.costpriceGood;
                        }

                        if (!isEmpty(meat)) {
                            const meatData = reduceWokComponents(meat, goodId);
                            if (!isEmpty(meatData)) {
                                if (meatData.count) goodAcc.count += meatData.count;
                                if (meatData.sum) goodAcc.sum += meatData.sum;
                                if (meatData.costpriceGood) goodAcc.costpriceGood += meatData.costpriceGood;
                            }
                        }

                        if (!isEmpty(topping)) {
                            const toppingData = reduceWokComponents(topping, goodId);
                            if (!isEmpty(toppingData)) {
                                if (toppingData.count) goodAcc.count += toppingData.count;
                                if (toppingData.sum) goodAcc.sum += toppingData.sum;
                                if (toppingData.costpriceGood) goodAcc.costpriceGood += toppingData.costpriceGood;
                            }
                        }

                        if (!isEmpty(souce)) {
                            const souceData = reduceWokComponents(souce, goodId, item.count);
                            if (!isEmpty(souceData)) {
                                if (souceData.count) goodAcc.count += souceData.count;
                                if (souceData.sum) goodAcc.sum += souceData.sum;
                                if (souceData.costpriceGood) goodAcc.costpriceGood += souceData.costpriceGood;
                            }
                        }

                        return goodAcc;
                    }, { sum: 0, count: 0, costpriceGood: 0 });

                    acc.count += dataOrderGoods.count || 0;
                    acc.costpriceGood += dataOrderGoods.costpriceGood || 0;
                    acc.sum += dataOrderGoods.sum || 0;
                }

                return acc;
            }, { sum: 0, count: 0, costpriceGood: 0 });

            if (!isEmpty(orderData)) {
                let averageCostprice = good.costprice || null;
                if (orderData.costpriceGood && orderData.count) averageCostprice = parseNumToFixed(orderData.costpriceGood / orderData.count);
                return {
                    ...good,
                    sum: orderData.sum || 0,
                    count: orderData.count || 0,
                    costpriceGood: orderData.costpriceGood || 0,
                    averageCostprice
                }
            }
            return { ...good, averageCostprice: good.costprice};
        });

        const goodWithAverageData = goodWithOrderData.map((good) => {
            const entity = { ...good };
            if (entity.count) {
                if (entity.sum) entity.averagePrice = parseNumToFixed(entity.sum / entity.count);
                if (renderFilters.points && renderFilters.points.length > 0) {
                    entity.averagePoint = parseNumToFixed(entity.count / renderFilters.points.length);
                } else if (renderFilters.regionalDirectors && renderFilters.regionalDirectors.length > 0) {
                    let dirsPointsCount = 0;
                    renderFilters.regionalDirectors.forEach((dir) => {
                        if (dir && dir.points && dir.points.length > 0) dirsPointsCount += dir.points.length;
                    });
                    entity.averagePoint = parseNumToFixed(entity.count / dirsPointsCount);
                } else {
                    entity.averagePoint = parseNumToFixed(entity.count / pointsCount);
                }
            }
            if (entity.averageCostprice && entity.averagePrice) entity.foodCost = parseNumToFixed((entity.averageCostprice / entity.averagePrice) * 100);
            if (entity.averageCostprice && entity.averagePrice) entity.averageMargin = parseNumToFixed(entity.averagePrice - entity.averageCostprice);
            return entity;
        });

        const getRangGoods = (goods, field, sort, rangs, resultName) => {
            return goods.sort((a, b) => sort === 'DESC' ? a[field] - b[field] : b[field] - a[field]).map((good, index) => {
                if (index + 1 <= rangs.A) return { ...good, [resultName]: 'A'};
                if (index + 1 > rangs.A && index + 1 <= rangs.B) return { ...good, [resultName]: 'B'};
                return { ...good, [resultName]: 'C'};
            });
        }

        const groupedGoods = groupBy(goodWithAverageData, (item) => item && (item.category || ""));
        const resultGoods = Object.values(groupedGoods).flatMap((goods) => {
            if (!goods || !goods.length) return [];
            const count = goods.length;
            const rangs = {
                A: count === 1 ? 1 : Math.floor(count * 0.5),
                B: Math.floor(count * 0.8),
            }
            const goodsWithFC = getRangGoods(goods, 'foodCost', 'DESC', rangs, 'foodCostABC');
            const goodsWithCount = getRangGoods(goodsWithFC, 'count', 'ASC', rangs, 'countABC');
            const goodsWithMargin = getRangGoods(goodsWithCount, 'averageMargin', 'ASC', rangs, 'marginABC');
            return goodsWithMargin;
        }).filter((item) => item);

        return { goods: resultGoods };
    }

    const onExport = () => {
        const { goods } = tableData;

        if (isEmpty(goods)) return swal({
            title: "Нет данных для экспорта",
            icon: "error",
            button: "Ок",
        });

        const { field, sort } = _sort;
        const exportData = goods.sort((a, b) => {
            const aField = a[field || 'category'];
            const bField = b[field || 'category'];

            if (aField == null && bField != null) return 1;
            if (aField != null && bField == null) return -1;
            if (aField == null && bField == null) return 0;
        
            if (sort === 'DESC') {
                if (typeof aField === 'string') {
                    const aLower = aField.toLowerCase();
                    const bLower = bField.toLowerCase();
                    if (aLower > bLower) return -1;
                    if (aLower < bLower) return 1;
                    return 0;
                }
                return bField - aField;
            } else {
                if (typeof aField === 'string') {
                    const aLower = aField.toLowerCase();
                    const bLower = bField.toLowerCase();
                    if (aLower < bLower) return -1;
                    if (aLower > bLower) return 1;
                    return 0;
                }
                return aField - bField;
            }
        }).map((good) => {
            return {
                'Категория': good.category || '-',
                'Наименование': good.name || '-',
                'Кол-во': good.countABC || '-',
                'FC': good.foodCostABC || '-',
                'Маржа': good.marginABC || '-'
            }
        });

        const ws = utils.json_to_sheet(exportData);
        const wb = utils.book_new();
        const periodText = `${moment(renderFilters.created_at_gte).format('DD.MM.YYYY')} - ${moment(renderFilters.created_at_lte).format('DD.MM.YYYY')}`;
        utils.book_append_sheet(wb, ws, 'ABC анализ меню');
        writeFileXLSX(wb, `ABC анализ меню ${periodText}.xlsx`);
    }

    const renderData = () => {
        if ((!result || result.length === 0) && (!renderFilters.points || renderFilters.points.length === 0) && (!renderFilters.regionalDirectors || renderFilters.regionalDirectors.length === 0)) return false;
        return true;       
    }

    const renderHead = () => {
        return(
            <div className="kt-portlet__head" style={{ flexDirection: 'column', gap: '10px' }}>
                <div className="kt-portlet__head-label">
                    <h3 className="kt-portlet__head-title">
                        ABC Анализ
                    </h3>
                </div>
                <div className='kt-separator kt-separator--border-dashed kt-separator--space-sm'/>
                <div><h6 style={{ color: 'black'}}> Поиск </h6></div>
                <div className="kt-portlet__head-toolbar" style={{ flexWrap: 'wrap', gap: '10px' }}>
                    <div style={{ width: 200 }}>
                        <label>Период *</label>
                        <DateSelect
                        isDisabled={loading}
                        isLoading={loading}
                        optionsDate={{
                            tommorow: true,
                            period: true,
                            prevWeek: true,
                            prevMonth: true,
                        }}
                        limit={{ 
                            field: 'months',
                            value: 6
                        }}
                        width={200}
                        onChange={onChangeDateSelect}
                        />
                    </div>
                    <div>
                        <label>Филиалы {(filters.regionalDirectors && filters.regionalDirectors.length > 0) ? '' : '*'}</label>
                        <PointsSelect width={200} placeholder='Филиалы' values={filters.points} setData={setFilters('points')} disabled={loading || (filters.regionalDirectors && filters.regionalDirectors.length > 0)}/>
                    </div>
                    <div style={{ width: 200 }}>
                        <label>Региональный директор {(filters.points && filters.points.length > 0) ? '' : '*'}</label>
                        <MultiSelect data={regionalDirectors} width={200} placeholder='Рег. директор' values={filters.regionalDirectors} setData={setFilters('regionalDirectors')} loading={loading} disabled={loading || (filters.points && filters.points.length > 0)}/>
                    </div>
                    <div style={{ display: 'flex', gap: '10px' }}>
                        <button disabled={isDisabledSearch()} onClick={onSearch} style={{ marginTop: '1.9rem' }} className="btn btn-primary">Найти</button>
                        {renderData() && <button disabled={loading} onClick={onExport} style={{ marginTop: '1.9rem' }} className='btn btn-dark btn-icon-sm'>Экспорт</button>}
                    </div>
                </div>
            </div>
        );
    }

    const renderTable = () => {
        const { field, sort } = _sort;
        const { goods } = tableData;

        return(
            <div className='kt-datatable kt-datatable--default kt-datatable--brand kt-datatable--loaded' style={{ overflowX: 'auto', maxWidth: '100%' }}>
                <table className='kt-datatable__table' style={{ width: '100%'}}>
                    <thead className='kt-datatable__head'>
                        <tr className='kt-datatable__row'>
                            <th className='kt-datatable__cell' style={{ width: '170px' }}>
                                <SortButton
                                className={field === 'category' ? 'btn btn-sm btn-primary' : 'btn btn-sm btn-secondary'} 
                                text='Категория' onClick={() => handleClickSort('category')} 
                                up={field === 'category' && sort === 'ASC'}
                                />
                            </th>
                            <th className='kt-datatable__cell' style={{ width: 'auto' }}>
                                <SortButton
                                className={field === 'name' ? 'btn btn-sm btn-primary' : 'btn btn-sm btn-secondary'} 
                                text='Наименование' onClick={() => handleClickSort('name')} 
                                up={field === 'name' && sort === 'ASC'}
                                />
                            </th>
                            <th className='kt-datatable__cell' style={{ width: '150px' }}>
                                <SortButton
                                className={field === 'name' ? 'btn btn-sm btn-primary' : 'btn btn-sm btn-secondary'} 
                                text='Кол-во продаж' onClick={() => handleClickSort('countABC')} 
                                up={field === 'countABC' && sort === 'ASC'}
                                />
                            </th>
                            <th className='kt-datatable__cell' style={{ width: '150px' }}>
                                <SortButton
                                className={field === 'name' ? 'btn btn-sm btn-primary' : 'btn btn-sm btn-secondary'} 
                                text='FC' onClick={() => handleClickSort('foodCostABC')} 
                                up={field === 'foodCostABC' && sort === 'ASC'}
                                />
                            </th>
                            <th className='kt-datatable__cell' style={{ width: '150px' }}>
                                <SortButton
                                className={field === 'name' ? 'btn btn-sm btn-primary' : 'btn btn-sm btn-secondary'} 
                                text='Маржа' onClick={() => handleClickSort('marginABC')} 
                                up={field === 'marginABC' && sort === 'ASC'}
                                />
                            </th>
                        </tr>
                    </thead>
                    <tbody className='kt-datatable__body'>
                        {goods && goods.length > 0 && goods.sort((a, b) => {
                        const aField = a[field || 'category'];
                        const bField = b[field || 'category'];

                        if (aField == null && bField != null) return 1;
                        if (aField != null && bField == null) return -1;
                        if (aField == null && bField == null) return 0;
                    
                        if (sort === 'DESC') {
                            if (typeof aField === 'string') {
                                const aLower = aField.toLowerCase();
                                const bLower = bField.toLowerCase();
                                if (aLower > bLower) return -1;
                                if (aLower < bLower) return 1;
                                return 0;
                            }
                            return bField - aField;
                        } else {
                            if (typeof aField === 'string') {
                                const aLower = aField.toLowerCase();
                                const bLower = bField.toLowerCase();
                                if (aLower < bLower) return -1;
                                if (aLower > bLower) return 1;
                                return 0;
                            }
                            return aField - bField;
                        }
                    }).map((item, index) => <ItemRow key={index} item={item} />)}
                    </tbody>
                </table>
            </div>
        );
    }

    const renderBody = () => {
        if (loading || pointLoading || goodsLoading) return <div style={styles.loading}><Loader /></div>;        

        if (!renderData()) return <div className='kt-portlet'> 
            <div className='kt-portlet__body'>Нет данных</div> 
        </div>

        return(
        <div className="kt-portlet">
            <ErrorContainer field="abcAnalysis" style={{ margin: '20px 20px 0px 20px' }} hasCloseButton={true}/>
            <div className='kt-portlet__body'>
            {renderTable()}
            </div>
        </div>
        );
    }

    return (
        <>
            <div className="kt-portlet">
                {renderHead()}
            </div>
            {renderBody()}
        </>         
    );
}